export interface ArticleBlock {
  type: "p" | "h" | "quote" | "list";
  text?: string;
  items?: string[];
}

export interface TimelineItem {
  date: string;
  text: string;
}

export interface Article {
  id: string;
  category: string;
  categorySlug: string;
  title: string;
  excerpt: string;
  cover: string;
  author: string;
  date: string;
  views: number;
  comments: number;
  readingMinutes: number;
  tags: string[];
  source: string;
  featured: boolean;
  hot: boolean;
  timeline: TimelineItem[];
  evidence: string[];
  content: ArticleBlock[];
  related: string[];
}

export const articles: Article[] = [
  {
    id: "a1",
    category: "影视",
    categorySlug: "film",
    title: "未播剧集换角风波：片方、经纪与演员的三方博弈",
    excerpt:
      "一部已经杀青的古装剧在定档前突然调整主演番位，公告只写了「档期协调」。我们把三份可交叉验证的时间线和合同条款摆在一起，看看这场换角到底卡在哪一步。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20empty%20film%20studio%20backstage%20with%20crimson%20velvet%20curtain%20and%20single%20spotlight%2C%20moody%20editorial%20photography%2C%20warm%20dark%20tones%2C%20blurred%20silhouette%20of%20a%20chair%2C%20cinematic%20atmosphere&width=1200&height=750&seq=hlbg-film-01&orientation=landscape",
    author: "扒瓜小组 · 阿凛",
    date: "2026-09-08",
    views: 128400,
    comments: 682,
    readingMinutes: 7,
    tags: ["换角", "合同条款", "番位"],
    source: "三方公开声明 + 剧组通告单交叉比对",
    featured: true,
    hot: true,
    timeline: [
      { date: "3 月 12 日", text: "剧组官宣主演阵容，主演顺序与备案公示一致。" },
      { date: "5 月 28 日", text: "全组杀青，进入后期制作阶段。" },
      { date: "7 月 4 日", text: "发行方内部试片，反馈集中在男主角戏份比例。" },
      { date: "8 月 19 日", text: "片方发布「档期协调」公告，宣布主演阵容调整。" },
      { date: "9 月 1 日", text: "经纪方回应称「尊重合同约定」，未提具体条款。" },
    ],
    evidence: [
      "备案公示信息中的主演顺序，与开播海报的排序不一致。",
      "通告单显示补拍集中在 8 月上旬，共 11 个工作日。",
      "经纪方声明全文未出现「解约」二字，只提「合作方式调整」。",
      "发行方试片反馈文档被媒体获得，其中「戏份比例」被标红。",
    ],
    content: [
      {
        type: "p",
        text: "这场换角风波从公告发出一刻起就争议不断。片方给的官方口径只有一句「因档期协调，主演阵容有所调整」，但一份 11 个工作日的补拍通告单，和试片会上被标红的「戏份比例」，说明事情没那么简单。",
      },
      { type: "h", text: "一、公告里被省掉的那段话" },
      {
        type: "p",
        text: "对比备案公示与开播海报，可以清楚看到主演排序发生了变化。行业里番位从来不只是名字先后，它直接绑定宣传资源、路演场次和后续分账谈判的权重。这也是为什么各方对排序如此敏感。",
      },
      { type: "h", text: "二、补拍通告单暴露的时间差" },
      {
        type: "p",
        text: "按照正常流程，杀青后的补拍通常围绕特效替身或空镜展开，规模有限。而这次补拍集中在 8 月上旬，涉及男女主对手戏的重拍，意味着剧本层面已经动过刀。",
      },
      {
        type: "list",
        items: [
          "补拍周期：8 月 3 日至 8 月 17 日，共 11 个工作日。",
          "涉及场景：男女主定情戏、朝堂对峙戏各重拍两版。",
          "现场人员：编剧到场，这在常规补拍中并不常见。",
        ],
      },
      { type: "h", text: "三、经纪方那句「尊重合同约定」的深意" },
      {
        type: "quote",
        text: "「我方始终尊重合同约定，也感谢剧组在合作方式上的坦诚沟通。」——经纪方声明节选",
      },
      {
        type: "p",
        text: "整份声明最值得琢磨的地方，是它从头到尾没有出现「解约」两个字。行业惯例里，如果确实是单方违约，经纪方通常会直接引用违约条款。回避这个词，往往意味着双方仍在履约框架内重新谈判。",
      },
      { type: "h", text: "四、这盘棋接下来会怎么走" },
      {
        type: "p",
        text: "从目前公开信息看，最可能的走向是「戏份调整 + 宣传番位后移」的组合方案，而不是彻底退出。对片方来说，补拍成本已经沉没；对经纪方来说，保住播出机会比撕破脸更划算。真正的看点，是后续路演名单和预告片里两人的镜头比例。",
      },
      {
        type: "p",
        text: "需要说明的是，以上推断均基于可交叉验证的公开材料与行业通行惯例，不指向任何具体自然人，具体结论仍以各方最终公告为准。",
      },
    ],
    related: ["a7", "a2", "a5"],
  },
  {
    id: "a2",
    category: "综艺",
    categorySlug: "variety",
    title: "慢综艺第二季口碑骤降：剪辑室里的三处关键删改",
    excerpt:
      "同一批嘉宾、同一个场地，为什么第二季的弹幕从「治愈」变成了「尴尬」？我们把两季的镜头时长做了统计，差异集中在这三个地方。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20empty%20television%20editing%20suite%20with%20warm%20amber%20desk%20lamp%20glow%2C%20multiple%20dark%20monitor%20screens%2C%20moody%20editorial%20photography%2C%20warm%20neutral%20tones%2C%20soft%20blur&width=1200&height=750&seq=hlbg-show-02&orientation=landscape",
    author: "扒瓜小组 · 老周",
    date: "2026-09-06",
    views: 96320,
    comments: 451,
    readingMinutes: 6,
    tags: ["综艺剪辑", "镜头时长", "口碑"],
    source: "两季成片镜头时长统计 + 现场探班记录",
    featured: true,
    hot: true,
    timeline: [
      { date: "第一季 5 月", text: "节目上线，口碑走高，豆瓣评分稳定在 8.4。" },
      { date: "第一季 8 月", text: "第一季收官，观众期待第二季原班回归。" },
      { date: "第二季 4 月", text: "第二季开播，前两期弹幕出现大量「节奏怪」反馈。" },
      { date: "第二季 6 月", text: "评分跌至 6.9，导演组在采访中提及「时长压缩」。" },
    ],
    evidence: [
      "第一季单期有效生活镜头约 34 分钟，第二季降至 19 分钟。",
      "第二季增加了大量解说与花字，平均每分钟出现 1.8 次。",
      "现场探班记录显示，两季拍摄机位数量一致。",
      "第二季嘉宾采访素材利用率不足三成。",
    ],
    content: [
      {
        type: "p",
        text: "慢综艺的命门在于「留白」。当一档主打治愈的节目开始害怕冷场，它就开始失去自己最有价值的部分。",
      },
      { type: "h", text: "一、生活镜头被切成碎片" },
      {
        type: "p",
        text: "统计两季的镜头时长后发现，第二季把大量长镜头改成了快切。原本一个完整做饭过程可以拍 90 秒，第二季被拆成 6 个两秒闪回，中间插入花字和解说。观众感受到的「节奏怪」，本质是叙事密度被强行拉高。",
      },
      { type: "h", text: "二、花字承担了本该由画面完成的工作" },
      {
        type: "list",
        items: [
          "第一季花字平均每分钟 0.4 次，第二季 1.8 次。",
          "解说词频繁替观众下结论，削弱了嘉宾真实反应的可信度。",
          "情绪高点被提前剧透，观众失去了自己发现细节的乐趣。",
        ],
      },
      { type: "h", text: "三、时长压缩背后的排播压力" },
      {
        type: "quote",
        text: "「不是我们不想留，是单期时长被压缩了，很多好素材只能舍掉。」——导演组采访节选",
      },
      {
        type: "p",
        text: "这句话其实点到了关键。当单期时长从 100 分钟压到 75 分钟，剪辑师必须在保留真实感和保证信息量之间做取舍。第二季选择了后者，代价就是那股慢下来的味道没了。",
      },
      { type: "h", text: "四、还有救吗" },
      {
        type: "p",
        text: "有。如果第三季愿意恢复长镜头比例、把花字减到第一季水平，并接受「无所事事也是内容」这个前提，口碑回弹并不难。毕竟观众要的从来不是更热闹，而是更松弛。",
      },
    ],
    related: ["a8", "a1", "a4"],
  },
  {
    id: "a3",
    category: "音乐",
    categorySlug: "music",
    title: "选秀歌手解约纠纷：一纸补充协议如何改变分账比例",
    excerpt:
      "解约官司年年有，但这一次的争议焦点不在违约金数字，而在一份被忽略的补充协议。它把原本 3:7 的分账，悄悄改成了 5:5。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20empty%20recording%20studio%20with%20vintage%20microphone%20and%20deep%20green%20acoustic%20panels%2C%20warm%20amber%20studio%20light%2C%20moody%20editorial%20photography%2C%20soft%20focus%20background&width=1200&height=750&seq=hlbg-music-03&orientation=landscape",
    author: "扒瓜小组 · 阿凛",
    date: "2026-09-04",
    views: 74500,
    comments: 318,
    readingMinutes: 6,
    tags: ["解约", "分账", "经纪合同"],
    source: "裁判文书摘要 + 双方代理律师公开表述",
    featured: false,
    hot: true,
    timeline: [
      { date: "2021 年", text: "选手以练习生身份签约，主合同约定分账比例 3:7。" },
      { date: "2022 年", text: "选手参加选秀出道，双方签署补充协议。" },
      { date: "2025 年", text: "选手单方提出解约，主张补充协议显失公平。" },
      { date: "2026 年", text: "案件进入调解阶段，双方就补充协议效力展开辩论。" },
    ],
    evidence: [
      "补充协议第 4 条约定「附加权益按 5:5 分配」，与主合同冲突。",
      "签约现场仅有经纪方代表与选手两人在场。",
      "选手方律师主张签署时未获得独立法律意见。",
      "经纪方提供了同期其他练习生的补充协议作为惯例佐证。",
    ],
    content: [
      {
        type: "p",
        text: "解约案里最常见的是违约金谈判，但这一回，双方把火力集中在一份只有两页纸的补充协议上。",
      },
      { type: "h", text: "一、主合同与补充协议打架，听谁的" },
      {
        type: "p",
        text: "主合同写的是演出收入按 3:7 分成，补充协议第 4 条却把「附加权益」单独拎出来按 5:5 分配。问题在于，「附加权益」的定义在补充协议里只有一句模糊表述，没有列举范围。",
      },
      { type: "h", text: "二、签约现场只有两个人" },
      {
        type: "p",
        text: "选手方的核心主张是：签署补充协议时，现场只有经纪方代表和选手本人，没有第三方见证，也没有给选手留出审阅时间。这在实务中常被作为「格式条款未充分提示」的论据。",
      },
      {
        type: "list",
        items: [
          "补充协议签署时间与主合同相隔 14 个月。",
          "协议正文未对「附加权益」作出定义或举例。",
          "经纪方以同期多份同类协议证明该条款为行业惯例。",
        ],
      },
      { type: "h", text: "三、行业惯例能不能成为挡箭牌" },
      {
        type: "quote",
        text: "「惯例只能证明做法普遍，不能证明条款当然有效。」——娱乐法从业者访谈观点",
      },
      {
        type: "p",
        text: "难点在于举证。经纪方拿出同期协议证明「大家都这么签」，但法院更关注的往往是签约时是否尽到提示义务，以及条款是否显著失衡。惯例是加分项，不是免死金牌。",
      },
      { type: "h", text: "四、这场纠纷真正的启示" },
      {
        type: "p",
        text: "对刚入行的年轻艺人来说，真正值钱的不是违约金数字，而是签字前那半小时的法律咨询。一份含糊的补充协议，足以把出道头三年的收入结构整个改写。",
      },
    ],
    related: ["a5", "a9", "a10"],
  },
  {
    id: "a4",
    category: "网红",
    categorySlug: "influencer",
    title: "从百万打赏到账号停更：直播间里的一场信任危机",
    excerpt:
      "停更公告只有八个字，但粉丝梳理出的时间线长达 40 条。打赏记录、抽奖名单和佣金结算单，把矛盾指向同一个环节。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20empty%20live%20streaming%20room%20with%20warm%20ring%20light%20glow%20and%20dark%20background%2C%20moody%20editorial%20photography%2C%20crimson%20and%20amber%20accents%2C%20soft%20blur%20bokeh&width=1200&height=750&seq=hlbg-live-04&orientation=landscape",
    author: "扒瓜小组 · 小满",
    date: "2026-09-02",
    views: 153600,
    comments: 921,
    readingMinutes: 5,
    tags: ["直播", "打赏", "抽奖争议"],
    source: "粉丝整理的公开截图汇总 + 平台规则对照",
    featured: false,
    hot: true,
    timeline: [
      { date: "6 月", text: "主播举办月度回馈抽奖，承诺 3 个工作日内寄出奖品。" },
      { date: "7 月", text: "多位中奖粉丝反馈未收到奖品，客服回复延迟。" },
      { date: "8 月", text: "主播发布八字停更公告，未说明原因。" },
      { date: "9 月", text: "粉丝整理 40 条时间线公开，直播间暂停更新。" },
    ],
    evidence: [
      "抽奖公示名单共 26 人，其中 9 人公开表示未收到奖品。",
      "客服聊天记录显示回复话术高度统一，疑似模板。",
      "停更公告发布前一天，账号仍挂有带货链接。",
      "平台规则明确要求抽奖结果公示并按时履约。",
    ],
    content: [
      {
        type: "p",
        text: "直播带货的信任是易碎品。一次没兑现的抽奖，可能抵消掉半年的内容积累。",
      },
      { type: "h", text: "一、抽奖名单上的缺口" },
      {
        type: "p",
        text: "公示名单一共 26 人，粉丝自发统计后发现有 9 人明确表示没有收到奖品，其余人多数处于「已发货但无物流」的状态。这已经不是个别疏漏，而是流程性瘫痪。",
      },
      { type: "h", text: "二、客服话术暴露的问题" },
      {
        type: "list",
        items: [
          "不同时间段的客服回复措辞几乎一致。",
          "始终没有给出明确的发货时间承诺。",
          "「正在核实」这句话在两个月内出现了 40 多次。",
        ],
      },
      { type: "h", text: "三、停更公告的时机很敏感" },
      {
        type: "quote",
        text: "「暂停更新，感谢陪伴。」——账号停更公告全文",
      },
      {
        type: "p",
        text: "八字公告没有道歉、没有解释、没有后续安排。更微妙的是，公告发布前一天，账号仍在挂带货链接。这种时间上的错位，是粉丝情绪彻底转向的导火索。",
      },
      { type: "h", text: "四、这类事件通常怎么收场" },
      {
        type: "p",
        text: "常见的处理路径是补发奖品、公开致歉、暂停抽奖类活动三个月。真正决定能否翻身的，不是补偿金额，而是能否拿出一个可核查的发放清单。信任重建靠的是可验证，不是漂亮话。",
      },
    ],
    related: ["a10", "a3", "a2"],
  },
  {
    id: "a5",
    category: "商业",
    categorySlug: "business",
    title: "艺人与品牌对簿：代言合同里被忽略的免责条款",
    excerpt:
      "品牌方撤下全部物料，艺人方要求按约支付尾款。争议的核心，是合同附件里那一条几乎没人细看的「道德条款」。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20empty%20minimal%20office%20desk%20with%20signed%20contract%20papers%20and%20folded%20glasses%2C%20warm%20cream%20and%20deep%20green%20tones%2C%20moody%20editorial%20photography%2C%20soft%20shadow&width=1200&height=750&seq=hlbg-biz-05&orientation=landscape",
    author: "扒瓜小组 · 老周",
    date: "2026-08-30",
    views: 61800,
    comments: 245,
    readingMinutes: 6,
    tags: ["代言", "道德条款", "商务合作"],
    source: "双方公开声明 + 合同附件条款摘要",
    featured: false,
    hot: false,
    timeline: [
      { date: "3 月", text: "双方签署年度代言合作，约定分两期支付。" },
      { date: "6 月", text: "品牌方下架全部艺人相关物料。" },
      { date: "7 月", text: "艺人方发函要求支付第二期款项。" },
      { date: "8 月", text: "品牌方援引道德条款拒绝支付，争议公开化。" },
    ],
    evidence: [
      "合同附件第 9 条为道德条款，表述为「有损品牌形象之行为」。",
      "品牌方下架物料时未出具书面说明。",
      "艺人方主张该条款缺乏客观判断标准。",
      "同期该品牌未更换其他代言人。",
    ],
    content: [
      {
        type: "p",
        text: "道德条款几乎出现在每一份代言合同里，但很多人都默认它只是走个流程。直到它真的被拿出来用。",
      },
      { type: "h", text: "一、什么叫「有损品牌形象」" },
      {
        type: "p",
        text: "附件第 9 条的原文是「如艺人存在有损品牌形象之行为，品牌方有权单方终止并停止付款」。问题在于，它没有给出任何判断标准——没有量化、没有列举、没有争议解决机制。这种模糊表述在实践中极易被滥用。",
      },
      { type: "h", text: "二、下架物料这件事本身就是证据" },
      {
        type: "list",
          items: [
          "品牌方下架时未同步发送书面解约通知。",
          "同期品牌并未启用新的代言人进行替换。",
          "电商详情页在两周内恢复上架，但未恢复主图露出。",
        ],
      },
      { type: "h", text: "三、免责条款不是万能钥匙" },
      {
        type: "quote",
        text: "「模糊条款的举证责任，通常在主张免责的一方。」——商务合同从业者观点",
      },
      {
        type: "p",
        text: "品牌方如果要靠道德条款免责，需要证明「有损形象」的事实客观存在且达到显著程度。只靠下架动作本身，很难构成充分举证。这也是艺人方敢于发函索款的底气。",
      },
      { type: "h", text: "四、给双方的一句提醒" },
      {
        type: "p",
        text: "代言合同的道德条款应当尽可能具体：列举情形、设定判断主体、约定争议解决路径。写得越模糊，真出事时越难用。",
      },
    ],
    related: ["a3", "a1", "a9"],
  },
  {
    id: "a6",
    category: "情感",
    categorySlug: "emotion",
    title: "两位演员的公开同框：从营销合作到私下关系的传闻梳理",
    excerpt:
      "同框三次、同款两次、行程重叠一次。粉丝把这些「巧合」串成了故事，但冷静看，其中至少有一半可以用工作安排解释。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20soft%20warm%20corridor%20with%20two%20distant%20blurred%20figures%20and%20amber%20window%20light%2C%20minimal%20editorial%20photography%2C%20warm%20neutral%20cream%20tones%2C%20gentle%20bokeh&width=1200&height=750&seq=hlbg-emo-06&orientation=landscape",
    author: "扒瓜小组 · 小满",
    date: "2026-08-27",
    views: 187300,
    comments: 1402,
    readingMinutes: 5,
    tags: ["同框", "传闻梳理", "理性吃瓜"],
    source: "公开行程与活动通告对照",
    featured: false,
    hot: true,
    timeline: [
      { date: "4 月", text: "两人共同出席同品牌发布会，被媒体拍到同框。" },
      { date: "6 月", text: "网友发现两人穿同款外套，引发讨论。" },
      { date: "7 月", text: "两人先后出现在同一城市，行程相差一天。" },
      { date: "8 月", text: "双方工作室均未回应相关传闻。" },
    ],
    evidence: [
      "三次同框中，两次为品牌方统一安排的公开活动。",
      "同款外套为该季热销单品，电商月销超 2 万件。",
      "同城行程分别对应各自剧组开机与收工。",
      "双方工作室对传闻保持一致的「不回应」策略。",
    ],
    content: [
      {
        type: "p",
        text: "吃瓜最忌讳的就是把巧合当证据。这一组传闻，我们逐条拆开看。",
      },
      { type: "h", text: "一、同框地点决定了可信度" },
      {
        type: "p",
        text: "三次同框里，有两次发生在品牌方的公开活动上，座位由主办方统一安排。也就是说，这类同框的「巧合」成分几乎为零——他们本来就会被安排在同一排。",
      },
      { type: "h", text: "二、同款这件事，其实最不具说服力" },
      {
        type: "list",
        items: [
          "涉事单品为当季热销款，多个平台的月销量均在万件以上。",
          "艺人有品牌方赞助服装的义务，撞款概率天然偏高。",
          "两人穿着时间相差近三周，并非同期。",
        ],
      },
      { type: "h", text: "三、行程重叠的另一种解释" },
      {
        type: "quote",
        text: "「同城不等于同场，行程相差一天更需要谨慎解读。」",
      },
      {
        type: "p",
        text: "两人分别出现在同一城市，时间相差一天，且各自对应剧组开机和收工。这种行程重叠在影视行业高发期非常常见——毕竟能拍戏的城市就那么几个。",
      },
      { type: "h", text: "四、不回应，也是一种回应" },
      {
        type: "p",
        text: "双方工作室都没有正面回应，这在业内是最常规的处理方式。回应反而会放大讨论。所以，在没有更硬的证据之前，这件事最合理的结论是：目前无法证实，保持观望。",
      },
    ],
    related: ["a4", "a8", "a2"],
  },
  {
    id: "a7",
    category: "影视",
    categorySlug: "film",
    title: "古装剧路透外泄：剧组安保与代拍之间的灰色链条",
    excerpt:
      "一张提前流出的定妆照，可能对应着一条从围栏缺口到社交平台变现的完整链路。我们复原了剧组追赶代拍的那 20 分钟。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20ancient%20chinese%20palace%20corridor%20at%20dusk%20with%20deep%20green%20tiles%20and%20amber%20lantern%20glow%2C%20empty%20scene%2C%20moody%20editorial%20photography%2C%20warm%20dark%20tones%2C%20soft%20haze&width=1200&height=750&seq=hlbg-film-07&orientation=landscape",
    author: "扒瓜小组 · 阿凛",
    date: "2026-08-24",
    views: 54200,
    comments: 198,
    readingMinutes: 5,
    tags: ["路透", "代拍", "剧组安保"],
    source: "剧组通告 + 现场人员转述",
    featured: false,
    hot: false,
    timeline: [
      { date: "7 月 9 日", text: "剧组转入外景地，加装两道临时围挡。" },
      { date: "7 月 15 日", text: "定妆照在社交平台提前流出，未被官方发布。" },
      { date: "7 月 16 日", text: "剧组临时调整拍摄计划，将当日外景改为内景。" },
      { date: "7 月 18 日", text: "现场发生追逐事件，安保方称「发现可疑人员」。" },
    ],
    evidence: [
      "外景地围挡存在一处未封闭的检修通道。",
      "流出的定妆照拍摄角度对应围挡外侧高点。",
      "同日代拍账号发布同类角度照片，时间相近。",
      "剧组当日通告被临时修改，改动痕迹明显。",
    ],
    content: [
      {
        type: "p",
        text: "路透早已不是偶发事件，而是一条有供需的产业链。这次的事件，恰好把链路的一头一尾都露了出来。",
      },
      { type: "h", text: "一、那道没封上的检修通道" },
      {
        type: "p",
        text: "外景地加装了两道临时围挡，但靠近西侧的检修通道因为施工原因始终没有封闭。流出的定妆照角度，正对应这条通道外侧的一处高点。",
      },
      { type: "h", text: "二、从拍摄到变现，可能只要几小时" },
      {
        type: "list",
        items: [
          "路透照片通常先在私域群流传，再流向公开平台。",
          "代拍定价按「独家程度」浮动，独家图溢价明显。",
          "部分账号以「粉丝福利」名义发布，实际带有引流目的。",
        ],
      },
      { type: "h", text: "三、剧组为什么选择改通告" },
      {
        type: "quote",
        text: "「与其在泄露之后追责，不如先把当天计划改掉。」——现场人员转述",
      },
      {
        type: "p",
        text: "把外景改成内景，是最直接的止损方式。虽然会打乱后续排期，但相比更多物料提前流出，这个代价是可以接受的。",
      },
      { type: "h", text: "四、这个链条为什么难根除" },
      {
        type: "p",
        text: "只要有粉丝愿意为独家付费，需求端就会持续存在。真正有效的办法不是增加围挡，而是让官方物料的发布节奏更密集，把「稀缺性」这个溢价基础拆掉。",
      },
    ],
    related: ["a1", "a6", "a8"],
  },
  {
    id: "a8",
    category: "综艺",
    categorySlug: "variety",
    title: "综艺常驻阵容调整背后：档期、片酬与剪辑权的拉扯",
    excerpt:
      "官宣名单和录制现场对不上，缺的不是一个人。当档期、片酬和剪辑权三件事同时挤在一起，节目组的取舍往往比想象中难。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20empty%20variety%20show%20stage%20with%20deep%20green%20backdrop%20and%20warm%20amber%20strip%20lights%2C%20minimal%20editorial%20photography%2C%20moody%20atmosphere%2C%20soft%20blur&width=1200&height=750&seq=hlbg-show-08&orientation=landscape",
    author: "扒瓜小组 · 老周",
    date: "2026-08-21",
    views: 47900,
    comments: 176,
    readingMinutes: 5,
    tags: ["常驻阵容", "片酬", "剪辑权"],
    source: "录制现场路透与官宣物料对照",
    featured: false,
    hot: false,
    timeline: [
      { date: "5 月", text: "节目官宣新一季常驻阵容，共 7 人。" },
      { date: "6 月", text: "首期录制现场仅出现 5 位常驻嘉宾。" },
      { date: "7 月", text: "节目组回应称「档期协调中」。" },
      { date: "8 月", text: "官宣名单更新，两位嘉宾转为「飞行」身份。" },
    ],
    evidence: [
      "首期录制的现场路透与官宣名单存在两人差异。",
      "两位嘉宾的行程表在录制期间有其他通告。",
      "节目正片中两人镜头合计不足 6 分钟。",
      "「飞行」身份通常意味着宣传资源显著减少。",
    ],
    content: [
      {
        type: "p",
        text: "一档节目的阵容表，其实是一份多方妥协后的结果。这次的调整，把三个老问题又摆到了台面上。",
      },
      { type: "h", text: "一、档期是最好用的理由，也是最常见的真问题" },
      {
        type: "p",
        text: "两位嘉宾在录制期间确实有其他通告在身，这一点从公开行程可以查到。但档期冲突通常是可以提前协调的，真正让它变成问题的，往往是后面两件事。",
      },
      { type: "h", text: "二、片酬与镜头分配几乎成正比" },
      {
        type: "list",
        items: [
          "常驻合同通常约定最低出镜时长。",
          "转为「飞行」后，该约定自动失效。",
          "正片中两人镜头合计不足 6 分钟，佐证了这一变化。",
        ],
      },
      { type: "h", text: "三、剪辑权才是真正的隐形筹码" },
      {
        type: "quote",
        text: "「谁决定镜头给谁，谁就掌握了话语权。」",
      },
      {
        type: "p",
        text: "剪辑权很少出现在合同明面上，但它决定了嘉宾最终呈现的形象。当一方对剪辑倾向不满，却又无法改变时，「档期不合」往往是最体面的退出方式。",
      },
      { type: "h", text: "四、观众其实只关心一件事" },
      {
        type: "p",
        text: "相比阵容怎么变，观众更在意节目好不好看。如果内容足够扎实，少两个人并不会伤筋动骨；反之，再豪华的名单也留不住人。",
      },
    ],
    related: ["a2", "a7", "a1"],
  },
  {
    id: "a9",
    category: "音乐",
    categorySlug: "music",
    title: "巡回演唱会取消风波：票务、主办与场馆的责任划分",
    excerpt:
      "开唱前 72 小时宣布取消，退票公告只写了「不可抗力」。当票务、主办和场馆各执一词，观众的钱和损失该找谁？",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20empty%20concert%20arena%20seats%20rows%20with%20warm%20amber%20emergency%20light%2C%20minimal%20editorial%20photography%2C%20moody%20dark%20tones%2C%20soft%20focus&width=1200&height=750&seq=hlbg-music-09&orientation=landscape",
    author: "扒瓜小组 · 小满",
    date: "2026-08-18",
    views: 88900,
    comments: 512,
    readingMinutes: 6,
    tags: ["演唱会取消", "退票", "责任划分"],
    source: "三方公告 + 票务平台退票规则",
    featured: false,
    hot: true,
    timeline: [
      { date: "7 月 2 日", text: "主办方宣布巡演城市站次，开票首日售罄。" },
      { date: "7 月 28 日", text: "场馆方通知舞台承重检测需延期。" },
      { date: "7 月 30 日", text: "距离开唱 72 小时，主办方宣布取消。" },
      { date: "8 月 3 日", text: "票务平台启动自动退票，未涉及机酒补偿。" },
    ],
    evidence: [
      "场馆方通知发布于宣布取消前两天。",
      "票务平台规则中，机酒损失不在赔付范围。",
      "主办方公告将原因归为「不可抗力」。",
      "同批次其他城市站次未受影响，照常开演。",
    ],
    content: [
      {
        type: "p",
        text: "演唱会取消最伤人的，从来不是退票本身，而是那些退不掉的机酒和请假。这一次，三方公告把责任推得很干净。",
      },
      { type: "h", text: "一、「不可抗力」这四个字用得太随意" },
      {
        type: "p",
        text: "场馆方在两日前就发出了承重检测延期的通知，而主办方的取消公告仍将原因归为「不可抗力」。严格来说，可预见的排期失误并不构成不可抗力。这个措辞上的偷懒，直接影响后续赔付认定。",
      },
      { type: "h", text: "二、退票规则里的缺口" },
      {
        type: "list",
        items: [
          "票面金额可全额退回，通常 7 个工作日内到账。",
          "机酒、请假等衍生损失不在平台赔付范围。",
          "主办方是否另行补偿，取决于其自愿。",
        ],
      },
      { type: "h", text: "三、为什么其他城市照常开演" },
      {
        type: "quote",
        text: "「同批次其他站次没有受到任何影响。」——主办方公告节选",
      },
      {
        type: "p",
        text: "这句话的解释力其实是双刃的。它既说明问题具有局部性，也暗示这更可能是单场馆的协调失误，而非行业级别的不可抗力。对观众来说，这一点正是难以接受的地方。",
      },
      { type: "h", text: "四、观众能做什么" },
      {
        type: "p",
        text: "保留购票凭证、机酒订单和沟通记录，是后续主张合理补偿的基础。虽然流程麻烦，但这是目前唯一被验证有效的方式。",
      },
    ],
    related: ["a3", "a5", "a10"],
  },
  {
    id: "a10",
    category: "网红",
    categorySlug: "influencer",
    title: "内容团队出走事件：账号归属与分成协议的争议焦点",
    excerpt:
      "一个百万粉账号，编剧、剪辑、出镜人全部换过一轮。离职团队晒出的分成表显示，真正拿走大头的是谁？",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20empty%20creative%20studio%20desk%20with%20deep%20green%20wall%20and%20warm%20amber%20desk%20light%2C%20camera%20and%20laptop%20silhouette%2C%20moody%20editorial%20photography%2C%20soft%20blur&width=1200&height=750&seq=hlbg-live-10&orientation=landscape",
    author: "扒瓜小组 · 阿凛",
    date: "2026-08-15",
    views: 71200,
    comments: 289,
    readingMinutes: 5,
    tags: ["账号归属", "分成协议", "团队纠纷"],
    source: "离职团队提供的内部分成表 + 账号运营数据",
    featured: false,
    hot: false,
    timeline: [
      { date: "2024 年", text: "三人小团队搭建账号，约定按 4:3:3 分配收益。" },
      { date: "2025 年", text: "账号粉丝破百万，主理人注册成立公司。" },
      { date: "2026 年 5 月", text: "两名核心成员离职，账号主体仍为公司持有。" },
      { date: "2026 年 7 月", text: "离职成员公开分成表，争议进入公众视野。" },
    ],
    evidence: [
      "分成表中「运营成本」一项占比接近六成。",
      "账号注册主体为后成立的公司，而非初始团队。",
      "初始口头约定的 4:3:3 未落成书面协议。",
      "离职后账号仍在正常更新，内容风格大幅调整。",
    ],
    content: [
      {
        type: "p",
        text: "内容团队的散伙，十次里有九次败在同一件事上：当初说好的，没有写下来。",
      },
      { type: "h", text: "一、那张分成表最刺眼的一行" },
      {
        type: "p",
        text: "分成表里「运营成本」占到了接近六成，包含设备折旧、场地、投流等条目。问题在于，这些条目既没有明细标准，也没有事前约定。当成本可以被单方定义时，分成比例就失去了意义。",
      },
      { type: "h", text: "二、账号主体决定了话语权" },
      {
        type: "list",
        items: [
          "账号初始注册在个人名下，后迁移至公司主体。",
          "迁移过程未取得全体成员书面同意。",
          "离职后账号仍正常更新，说明控制权未发生转移。",
        ],
      },
      { type: "h", text: "三、口头约定为什么站不住" },
      {
        type: "quote",
        text: "「4:3:3 只是当时的说法，没有写进任何文件。」——离职成员陈述",
      },
      {
        type: "p",
        text: "口头约定的最大问题是举证难。一旦对方否认，主张方需要提供聊天记录、转账记录等佐证，而这些往往零散、不完整。这也是大量内容团队纠纷最终不了了之的原因。",
      },
      { type: "h", text: "四、给内容创业者的三条建议" },
      {
        type: "list",
        items: [
          "账号归属、成本口径、退出机制必须在开始时就书面明确。",
          "分成比例要与可核查的成本明细绑定，避免「口袋成本」。",
          "团队规模扩大前，先完成账号主体的权属梳理。",
        ],
      },
    ],
    related: ["a4", "a3", "a5"],
  },
];

export const getArticleById = (id: string): Article | undefined =>
  articles.find((item) => item.id === id);

export const featuredArticles = articles.filter((item) => item.featured);
export const hotArticles = articles.filter((item) => item.hot);
export const latestArticles = [...articles].sort((a, b) => (a.date < b.date ? 1 : -1));